import { cloneDeep, range } from "lodash";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import Stack from "veneer/Stack";
import Typography from "veneer/Typography";

import OperationScheduleCell from "./OperationScheduleCell";

interface OperationScheduleProps {
  onChange: (deviceOperateTime: string) => void;
  readOnly?: boolean;
  schedule?: string | undefined;
}

type DayNumber = 1 | 2 | 3 | 4 | 5 | 6 | 7;

interface DayOperate {
  activeHours: number[];
}

type DayOperateTime = Record<number, DayOperate>; // keys: 1..7

const DAYS: Record<DayNumber, string> = {
  1: "Monday",
  2: "Tuesday",
  3: "Wednesday",
  4: "Thursday",
  5: "Friday",
  6: "Saturday",
  7: "Sunday",
};

const NUM_HOURS: number[] = Array.from({ length: 24 }, (_, i) => i);

function OperationScheduleForm({
  onChange,
  readOnly = false,
  schedule,
}: OperationScheduleProps) {
  const [activeDay, setActiveDay] = useState<DayNumber | null>(null);
  const [dayOperateTime, setDayOperateTime] = useState<DayOperateTime>({});
  const [startHour, setStartHour] = useState<number | null>(null);
  const [shouldSelect, setShouldSelect] = useState<boolean | null>(null);
  const [hoverHour, setHoverHour] = useState<number | null>(null);

  const activeDayRef = useRef<HTMLDivElement | null>(null);

  const stringifyOperateParams = useCallback((dot: DayOperateTime): string => {
    return Object.entries(dot)
      .map(([day, params]) => `${day}-${params?.activeHours.join("-") ?? ""}`)
      .join(",");
  }, []);

  const parseOperateTime = useCallback(
    (operateTime?: string): DayOperateTime => {
      if (!operateTime) {
        return {};
      }

      // Example: "1-8-9-10-12-13-14" => day: 1, hours: [8, 9, 10, 12, 13, 14]
      const weekParams = operateTime.split(",");

      return weekParams.reduce<DayOperateTime>((acc, dayParam) => {
        const [dayStr, ...operateHours] = dayParam.split("-");
        const day = Number.parseInt(dayStr, 10) as DayNumber;

        if (!Number.isFinite(day) || day < 1 || day > 7) {
          return acc;
        }

        const hours = operateHours
          .map((h) => Number.parseInt(h, 10))
          .filter((h) => Number.isFinite(h) && h >= 0 && h <= 23);

        acc[day] = { activeHours: hours };

        return acc;
      }, {});
    },
    [],
  );

  const formatDeviceOperateTime = useCallback(
    (
      day: DayNumber,
      hours: number[],
      shouldSel: boolean,
      current: DayOperateTime,
    ): string => {
      const next = cloneDeep(current) as DayOperateTime;

      let activeHours = next[day]
        ? [...(next[day]!.activeHours ?? [])]
        : [...NUM_HOURS];

      if (!next[day]) {
        next[day] = { activeHours: [] };
      }

      if (shouldSel) {
        const toAdd = hours.filter((h) => !activeHours.includes(h));
        activeHours = activeHours.concat(toAdd);
      } else {
        activeHours = activeHours.filter((ah) => !hours.includes(ah));
      }

      activeHours.sort((a, b) => a - b);
      next[day] = { activeHours };

      return stringifyOperateParams(next);
    },
    [stringifyOperateParams],
  );

  useEffect(() => {
    setDayOperateTime(parseOperateTime(schedule));
  }, [schedule, parseOperateTime]);

  const resetDragState = useCallback(() => {
    setActiveDay(null);
    setStartHour(null);
    setShouldSelect(null);
    setHoverHour(null);
  }, []);

  const cancelSelectOnMouseup = useCallback(
    (e: MouseEvent) => {
      if (!activeDayRef.current) {
        return;
      }

      if (!activeDayRef.current.contains(e.target as Node)) {
        resetDragState();
        window.removeEventListener("mouseup", cancelSelectOnMouseup);
      }
    },
    [resetDragState],
  );

  useEffect(() => {
    // Ensure we never leave a dangling global listener on unmount.
    return () => {
      window.removeEventListener("mouseup", cancelSelectOnMouseup);
    };
  }, [cancelSelectOnMouseup]);

  const onStartSelect = useCallback(
    (day: DayNumber, hour: number, shouldSel: boolean) => {
      if (readOnly) {
        return;
      }

      setActiveDay(day);
      setStartHour(hour);
      setHoverHour(hour);
      setShouldSelect(shouldSel);

      window.addEventListener("mouseup", cancelSelectOnMouseup);
    },
    [readOnly, cancelSelectOnMouseup],
  );

  const onHoverHourHandler = useCallback(
    (hour: number) => {
      if (!activeDay) {
        return;
      }

      setHoverHour(hour);
    },
    [activeDay],
  );

  const onUpdateDay = useCallback(
    (day: DayNumber, hour: number) => {
      if (readOnly) {
        return;
      }

      const localStartHour = startHour;
      const localShouldSelect = shouldSelect;

      resetDragState();
      window.removeEventListener("mouseup", cancelSelectOnMouseup);

      if (localStartHour == null || localShouldSelect == null) {
        return;
      }

      const hoursRange = range(
        Math.min(localStartHour, hour),
        Math.max(localStartHour, hour) + 1,
      );

      const deviceOperateTime = formatDeviceOperateTime(
        day,
        hoursRange,
        localShouldSelect,
        dayOperateTime,
      );

      onChange(deviceOperateTime);
    },
    [
      readOnly,
      startHour,
      shouldSelect,
      resetDragState,
      cancelSelectOnMouseup,
      formatDeviceOperateTime,
      dayOperateTime,
      onChange,
    ],
  );

  const isSelectedHour = useCallback(
    (dayObj: DayOperate, hour: number): boolean => {
      return dayObj.activeHours.includes(hour);
    },
    [],
  );

  const getHoverStatus = useCallback(
    (
      day: DayNumber,
      hour: number,
    ): "selecting" | "deselecting" | false | undefined => {
      if (
        day !== activeDay ||
        startHour == null ||
        hoverHour == null ||
        shouldSelect == null
      ) {
        return;
      }

      const inRange =
        (startHour <= hour && hour <= hoverHour) ||
        (startHour >= hour && hour >= hoverHour);

      if (!inRange) {
        return false;
      }

      return shouldSelect ? "selecting" : "deselecting";
    },
    [activeDay, startHour, hoverHour, shouldSelect],
  );

  const daysList = useMemo(() => {
    return (Object.keys(DAYS) as string[]).map(
      (day) => Number.parseInt(day, 10) as DayNumber,
    );
  }, []);

  return (
    <div className="op-sched" data-testid="op-sched-form">
      {daysList.map((dayNum) => {
        const dayData: DayOperate = dayOperateTime[dayNum] || {
          activeHours: [...NUM_HOURS],
        };
        return (
          <Stack
            alignItems="center"
            direction="row"
            key={dayNum}
            mb={1}
            ref={(node: HTMLDivElement | null) => {
              if (dayNum === activeDay) {
                activeDayRef.current = node;
              }
            }}
            spacing={0.5}
          >
            <Typography width={100}>{DAYS[dayNum]}</Typography>
            {NUM_HOURS.map((hour) => {
              const selected = isSelectedHour(dayData, hour);
              const hoverStatus = getHoverStatus(dayNum, hour);
              return (
                <OperationScheduleCell
                  day={dayNum}
                  disabled={Boolean(activeDay && dayNum !== activeDay)}
                  hour={hour}
                  hoverStatus={hoverStatus}
                  isSelected={selected}
                  key={`${dayNum}--${hour}`}
                  onHover={() => onHoverHourHandler(hour)}
                  onMouseDown={() => onStartSelect(dayNum, hour, !selected)}
                  onMouseUp={() => onUpdateDay(dayNum, hour)}
                  readOnly={readOnly}
                />
              );
            })}
          </Stack>
        );
      })}
    </div>
  );
}

export { OperationScheduleForm };
export default OperationScheduleForm;
